'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import { useLocale } from 'next-intl'

import { ChevronUp, Search } from 'lucide-react'

import { browserNavigate } from '@/lib/browser-navigation'
import { formatShortDate } from '@/lib/c2c/format'
import type { SearchWidgetCopy } from '@/lib/search-widget/copy'
import { cn } from '@/lib/utils'
import { type DisplayDateAnchor } from '@/i18n/dates'

import { useSearchSelection } from './search-selection-context'
import { SearchWidgetClient } from './search-widget.client'
import { useSearchSubmit } from './use-search-submit'
import { useWidgetState } from './use-widget-state'

export interface StickySearchBarProps {
    initialFrom?: string
    initialFromId?: string
    initialTo?: string
    initialToId?: string
    initialMode?: string
    initialDate?: string
    /** Server-computed date anchor. Required unconditionally: mounted without a
     *  `SearchSelectionProvider` the bar owns its selection, and a missing anchor is
     *  then the React #418 regression. Under a provider the surface owns the selection
     *  and this anchor goes unused. invariants.md#ssr-safe-rendered-dates */
    dateAnchor: DisplayDateAnchor
}

/** The bar's interactive half, which the server-side `StickySearchBar` hands the
 *  widget's copy and forwards to the widget it expands into. */
export function StickySearchBarClient({
    copy,
    initialFrom,
    initialFromId,
    initialTo,
    initialToId,
    initialMode = 'bus',
    initialDate,
    dateAnchor,
}: StickySearchBarProps & { readonly copy: SearchWidgetCopy }) {
    const [visible, setVisible] = useState(false)
    const [expanded, setExpanded] = useState(false)
    const observerRef = useRef<IntersectionObserver | null>(null)
    const collapseTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
    const barRef = useRef<HTMLDivElement>(null)
    const mouseInBar = useRef(false)
    const mouseInPortal = useRef(false)
    const [mobileExpanded, setMobileExpanded] = useState(false)
    const locale = useLocale()

    // The surface-owned selection wins, so the bar and the page's other presentations
    // are one selection; mounted without a provider the bar owns its own.
    // Only the owner restores and persists — see `UseWidgetStateOptions.enabled`.
    const surfaceSelection = useSearchSelection()
    const ownSelection = useWidgetState(
        {
            from: initialFrom,
            fromId: initialFromId,
            to: initialTo,
            toId: initialToId,
            mode: initialMode as 'bus' | 'train' | 'ferry' | 'flight',
            // An unsupplied initialDate must stay `undefined` — a '' fallback is a
            // *defined* value that survives the undefined-only filter and clobbers
            // the restored stored date (#795).
            departDate: initialDate,
        },
        dateAnchor,
        { enabled: !surfaceSelection },
    )
    const widgetState = surfaceSelection ?? ownSelection

    const [state] = widgetState
    const { submitFromCollapsed } = useSearchSubmit(state, locale)

    const cancelCollapse = useCallback(() => {
        if (collapseTimer.current) {
            clearTimeout(collapseTimer.current)
            collapseTimer.current = null
        }
    }, [])

    const scheduleCollapse = useCallback(() => {
        cancelCollapse()
        collapseTimer.current = setTimeout(() => {
            if (!mouseInBar.current && !mouseInPortal.current) {
                setExpanded(false)
            }
        }, 200)
    }, [cancelCollapse])

    const handleMouseEnter = useCallback(() => {
        mouseInBar.current = true
        cancelCollapse()
        setExpanded(true)
    }, [cancelCollapse])

    const handleMouseLeave = useCallback(() => {
        mouseInBar.current = false
        scheduleCollapse()
    }, [scheduleCollapse])

    // Track mouse over Radix portal content (calendars, autocomplete dropdowns)
    useEffect(() => {
        if (!visible) {
            return
        }

        const handlePointerOver = (e: PointerEvent) => {
            const target = e.target as Element
            if (target.closest('[data-radix-popper-content-wrapper]')) {
                mouseInPortal.current = true
                cancelCollapse()
            }
        }

        const handlePointerOut = (e: PointerEvent) => {
            const target = e.relatedTarget as Element | null
            if (!target?.closest('[data-radix-popper-content-wrapper]')) {
                mouseInPortal.current = false
                scheduleCollapse()
            }
        }

        document.addEventListener('pointerover', handlePointerOver)
        document.addEventListener('pointerout', handlePointerOut)

        return () => {
            document.removeEventListener('pointerover', handlePointerOver)
            document.removeEventListener('pointerout', handlePointerOut)
        }
    }, [visible, cancelCollapse, scheduleCollapse])

    useEffect(() => {
        const hero = document.querySelector('[data-section="hero"]')
        if (!hero) {
            return
        }

        observerRef.current = new IntersectionObserver(
            ([entry]) => {
                setVisible(!entry.isIntersecting)
            },
            { threshold: 0 },
        )

        observerRef.current.observe(hero)

        return () => {
            observerRef.current?.disconnect()
        }
    }, [])

    useEffect(() => {
        return () => cancelCollapse()
    }, [cancelCollapse])

    if (!visible) {
        return null
    }

    const navigateToSearch = () => {
        const result = submitFromCollapsed()
        if (result.kind === 'expand') {
            setMobileExpanded(true)
            return
        }
        browserNavigate(result.url)
    }

    return (
        <div
            ref={barRef}
            data-testid="sticky-search-bar"
            id="sticky-search-bar"
            className="fixed top-[50px] right-0 left-0 z-20 overflow-hidden border-b border-[#d5dde6] bg-white shadow-[0_4px_8px_rgba(23,25,27,0.08)] md:top-0 md:z-40"
        >
            <div className="md:hidden">
                {mobileExpanded ? (
                    <div className="px-4 py-3">
                        <SearchWidgetClient
                            copy={copy}
                            widgetState={widgetState}
                            formId="sticky-mobile"
                        />
                        <button
                            type="button"
                            onClick={() => {
                                setMobileExpanded(false)
                            }}
                            aria-label={copy.collapseSearch.text}
                            className="mx-auto mt-2 flex items-center justify-center"
                        >
                            <ChevronUp className="h-6 w-6 text-[#8c939c]" />
                        </button>
                    </div>
                ) : (
                    <div className="flex items-center gap-2 px-4 py-2">
                        <button
                            type="button"
                            onClick={() => {
                                setMobileExpanded(true)
                            }}
                            className="flex min-w-0 flex-1 flex-col rounded-[7px] border border-[#d9dee5] px-3 py-1.5 text-left"
                        >
                            <span className="truncate text-sm font-bold text-[#2e3033]">
                                {state.from || state.to ? (
                                    <>
                                        {state.from}
                                        {state.from && state.to && (
                                            <span className="mx-1 font-normal text-[#8c939c]">
                                                &rsaquo;
                                            </span>
                                        )}
                                        {state.to}
                                    </>
                                ) : (
                                    copy.searchTrips.text
                                )}
                            </span>
                            <span className="text-xs text-[#5f6368]">
                                {state.departDate
                                    ? formatShortDate(state.departDate, locale)
                                    : copy.selectDate.text}
                                {state.tripType === 'round-trip' && state.returnDate && (
                                    <> - {formatShortDate(state.returnDate, locale)}</>
                                )}
                            </span>
                        </button>
                        <button
                            type="button"
                            onClick={navigateToSearch}
                            aria-label={copy.search.text}
                            className="flex h-14 w-14 shrink-0 items-center justify-center rounded-[7px] bg-[#d13b3b] text-white transition-colors hover:bg-[#b83232]"
                        >
                            <Search className="h-5 w-5" />
                        </button>
                    </div>
                )}
            </div>

            {/* Desktop hover-expand layout */}
            <div
                className={cn(
                    'hidden transition-[margin-top] duration-300 ease-in-out md:block',
                    expanded ? 'md:mt-0' : 'md:mt-[-36px]',
                )}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
            >
                <div className="mx-auto max-w-[980px] px-6 pt-2 pb-4">
                    <SearchWidgetClient copy={copy} widgetState={widgetState} formId="sticky" />
                </div>
            </div>
        </div>
    )
}
